import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Edit2, Trash2, AlertCircle, ChevronUp, ChevronDown } from "lucide-react";
import { SortableField } from "./sortable-field";
import { Field, Section } from "@/services/entity-view.service";

interface DroppableSectionProps {
  section: Section;
  onEditSection: (section: Section) => void;
  onDeleteSection: (sectionId: string) => void;
  onRemoveField: (sectionId: string, fieldId: string) => void;
  onUpdateFieldWidth: (sectionId: string, fieldId: string, width: 1 | 2 | 3) => void;
  onUpdateFieldHeight: (sectionId: string, fieldId: string, height: 1 | 2 | 3) => void;
  onUpdateRelationFieldDisplay?: (
    sectionId: string,
    fieldId: string,
    relationFieldToDisplay: string,
  ) => void;
  onMoveSection: (sectionId: string, direction: "up" | "down") => void;
  isFirst: boolean;
  isLast: boolean;
  isHovered: boolean;
  isDraggingAny: boolean;
  draggedField: Field | null;
  entityName?: string;
  entityRelations?: Array<{
    id: string;
    field_name: string;
    target_entity: {
      data: {
        id: string;
        name: string;
      };
    };
  }>;
  entityRelated?: Array<{
    id: string;
    field_name: string;
    target_field_name?: string;
    entity: {
      data: {
        id: string;
        name: string;
      };
    };
    target_entity: {
      data: {
        id: string;
        name: string;
      };
    };
  }>;
}

export function DroppableSection({
  section,
  onEditSection,
  onDeleteSection,
  onRemoveField,
  onUpdateFieldWidth,
  onUpdateFieldHeight,
  onUpdateRelationFieldDisplay,
  onMoveSection,
  isFirst,
  isLast,
  isHovered,
  isDraggingAny,
  draggedField,
  entityName,
  entityRelations,
  entityRelated,
}: DroppableSectionProps) {
  const { setNodeRef } = useDroppable({
    id: section.id,
    data: { section },
  });

  const hasFields = section.fields.length > 0;

  // Helper function to get relation data for a field
  const getRelationDataForField = (fieldId: string) => {
    // Find the field in the section to get its name
    const field = section.fields.find((f) => f.id === fieldId);
    if (!field) return undefined;

    // Check both entityRelations (outgoing) and entityRelated (incoming)
    const relationData = entityRelations?.find((relation) => relation.field_name === field.name);

    if (relationData) {
      return relationData;
    }

    // If not found in entityRelations, check entityRelated
    // For entityRelated, we need to match by target_field_name
    const relatedData = entityRelated?.find(
      (relation) => relation.target_field_name === field.name,
    );

    if (relatedData) {
      // For entityRelated, we want to show fields from the source entity (entity field)
      // not the target entity, so we need to modify the structure
      return {
        ...relatedData,
        target_entity: relatedData.entity, // Use source entity instead of target entity
      };
    }

    return undefined;
  };

  return (
    <Card key={section.id}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">{section.name}</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => onEditSection(section)}>
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onMoveSection(section.id, "up")}
                  disabled={isFirst}
                  className="h-8 w-8 p-0"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Move section up</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onMoveSection(section.id, "down")}
                  disabled={isLast}
                  className="h-8 w-8 p-0"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Move section down</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDeleteSection(section.id)}
                    disabled={hasFields}
                    className={`${
                      hasFields
                        ? "cursor-not-allowed opacity-50"
                        : "text-red-600 hover:text-red-700"
                    }`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {hasFields ? (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <p>Remove all fields before deleting this section</p>
                  </div>
                ) : (
                  <p>Delete section</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        <div
          ref={setNodeRef}
          className={`min-h-[200px] rounded-lg border-2 border-dashed p-4 transition-all duration-200 ${
            isHovered ? "border-blue-400 bg-blue-50 shadow-inner" : "border-gray-200 bg-gray-50"
          }`}
        >
          {section.fields.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <div className="text-sm">Drop fields here to add them to this section</div>
              {isHovered && draggedField && (
                <div className="mt-2 text-xs text-blue-600">
                  Drop {draggedField.name} ({draggedField.width}×{draggedField.height}) here
                </div>
              )}
            </div>
          ) : (
            <SortableContext items={section.fields.map((f) => f.id)} strategy={rectSortingStrategy}>
              <div className="grid auto-rows-[80px] grid-cols-3 gap-3">
                {section.fields.map((field) => (
                  <SortableField
                    key={field.id}
                    field={field}
                    sectionId={section.id}
                    onRemove={onRemoveField}
                    onUpdateWidth={onUpdateFieldWidth}
                    onUpdateHeight={onUpdateFieldHeight}
                    onUpdateRelationFieldDisplay={onUpdateRelationFieldDisplay}
                    isDraggingAny={isDraggingAny}
                    entityName={entityName}
                    relationData={getRelationDataForField(field.id)}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
